<script lang="ts">
	import PageHeader from '$lib/components/PageHeader.svelte';
	import Pill from '$lib/components/Pill.svelte';
	import { ArrowRight } from '@lucide/svelte';

	const studies = [
		{
			slug: 'parallel-agent-loops',
			eyebrow: 'The parallel paradigm',
			title: 'Staying coherent across parallel loops',
			blurb:
				'Driving several agent loops across projects at once, without losing the plan — using labels and notes as breadcrumbs to beat cognitive overload and the context-switching tax.',
			labels: [
				['red', 'repo: dial-in'],
				['blue', 'fix: sync-retry']
			]
		},
		{
			slug: 'joined-to-what-shipped',
			eyebrow: 'Records & invoicing',
			title: 'Time, joined to what shipped',
			blurb:
				'Correlating labeled agent-session spans with real commits and PRs, so internal records and client invoices follow delivered value instead of guesswork.',
			labels: [
				['red', 'repo: sfi/sfi-website'],
				['green', 'type: review']
			]
		}
	] as const;
</script>

<svelte:head>
	<title>Case studies — Moment Tally</title>
	<meta
		name="description"
		content="Moment Tally, in practice — how StreetFortress developers use it to track agent work, stay coherent across parallel loops, and tie time to delivered value."
	/>
</svelte:head>

<PageHeader
	eyebrow=""
	title="Moment Tally, in practice"
	lead="How StreetFortress developers actually use Moment Tally — tracking agent work, staying coherent across parallel loops, and joining time to what shipped."
/>

<div class="mx-auto max-w-4xl px-5 py-16">
	<div class="grid gap-5">
		{#each studies as s (s.slug)}
			<a
				href="/case-study/{s.slug}"
				class="card card-hover group relative overflow-hidden p-7 sm:p-8"
			>
				<div
					class="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full blur-2xl"
					style="background: radial-gradient(circle, rgb(0 122 255 / 0.1), transparent 60%)"
				></div>
				<div class="relative">
					<p class="font-mono text-xs uppercase tracking-widest text-accent">{s.eyebrow}</p>
					<h2 class="mt-2 font-display text-2xl font-extrabold tracking-tight sm:text-3xl">
						{s.title}
					</h2>
					<p class="mt-3 max-w-2xl leading-relaxed text-fg-muted">{s.blurb}</p>
					<div class="mt-5 flex flex-wrap items-center gap-2">
						{#each s.labels as [tone, text] (text)}
							<Pill tone={tone as 'red' | 'green' | 'blue' | 'amber'}>{text}</Pill>
						{/each}
						<span
							class="ml-auto inline-flex items-center gap-1.5 text-sm font-medium text-fg-muted transition-colors group-hover:text-accent"
						>
							Read the case study <ArrowRight size={15} />
						</span>
					</div>
				</div>
			</a>
		{/each}
	</div>
</div>
